// src/pages/manager/admin/NameImportPanel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { runNameImport, streamJobLogs, getJob } from "../../../lib/ligamanagerApi";

function clampLogs(arr, max = 400) {
    if (!Array.isArray(arr)) return [];
    if (arr.length <= max) return arr;
    return arr.slice(arr.length - max);
}

export default function NameImportPanel() {
    const [sheetId, setSheetId] = useState("");
    const [starting, setStarting] = useState(false);

    const [jobId, setJobId] = useState(null);
    const [jobState, setJobState] = useState("idle"); // idle | running | done | error
    const [progress, setProgress] = useState({ cur: 0, total: 0, label: "" });

    const [logs, setLogs] = useState([]);
    const [err, setErr] = useState("");

    // hier speichern wir die Stop-Funktion aus streamJobLogs()
    const stopStreamRef = useRef(null);
    const stopPollRef = useRef(null);

    const atBottomRef = useRef(true);
    const logBoxRef = useRef(null);

    const progressPct = useMemo(() => {
        const { cur, total } = progress || {};
        if (!total || total <= 0) return 0;
        return Math.max(0, Math.min(100, Math.round((cur / total) * 100)));
    }, [progress]);

    const stopStream = () => {
        try {
            if (typeof stopStreamRef.current === "function") {
                stopStreamRef.current();
            }
        } catch {}
        stopStreamRef.current = null;
    };

    const stopPolling = () => {
        try {
            if (stopPollRef.current) {
                clearInterval(stopPollRef.current);
            }
        } catch {}
        stopPollRef.current = null;
    };

    const resetUI = () => {
        stopStream();
        stopPolling();
        setJobId(null);
        setJobState("idle");
        setProgress({ cur: 0, total: 0, label: "" });
        setLogs([]);
        setErr("");
    };

    const appendLog = (entry) => {
        setLogs((prev) => clampLogs([...prev, entry]));
    };

    // auto-scroll Logbox wenn User unten ist
    useEffect(() => {
        const box = logBoxRef.current;
        if (!box) return;
        if (!atBottomRef.current) return;
        box.scrollTop = box.scrollHeight;
    }, [logs]);

    const onLogScroll = () => {
        const box = logBoxRef.current;
        if (!box) return;
        const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 24;
        atBottomRef.current = nearBottom;
    };

    const startJobPolling = (id) => {
        stopPolling();

        const pollOnce = async () => {
            try {
                const j = await getJob(id);

                setProgress({
                    cur: Number(j?.progress_step || 0),
                    total: Number(j?.progress_total || 0),
                    label: j?.progress_label || "",
                });

                const status = String(j?.status || "").toLowerCase();

                if (status === "done") {
                    setJobState("done");
                    stopPolling();
                } else if (status === "error" || status === "canceled") {
                    setJobState("error");
                    stopPolling();
                }
            } catch {
                // bewusst still; Logstream ist die Hauptquelle
            }
        };

        pollOnce();
        stopPollRef.current = setInterval(pollOnce, 700);
    };

    const startJob = async () => {
        const v = String(sheetId || "").trim();
        if (!v) {
            setErr("Bitte Sheet-ID oder URL eintragen.");
            return;
        }

        setErr("");
        setStarting(true);
        setJobState("idle");
        setProgress({ cur: 0, total: 0, label: "" });
        setLogs([]);

        stopStream();
        stopPolling();

        try {
            const data = await runNameImport({ sheetInput: v });

            if (!data?.job_id) {
                throw new Error("Backend hat kein job_id zurückgegeben.");
            }

            setJobId(data.job_id);
            setJobState("running");

            // Progress polling starten
            startJobPolling(data.job_id);

            // Log-Stream starten
            const stopFn = streamJobLogs({
                job_id: data.job_id,
                onLog: (item) => {
                    appendLog({
                        ts: item?.ts || null,
                        level: item?.level || "INFO",
                        msg: String(item?.msg || ""),
                    });
                },
                onDone: (status) => {
                    const s = String(status || "").trim().toLowerCase();

                    if (s === "done") {
                        setJobState("done");
                    } else {
                        setJobState("error");
                        setErr("Job mit Fehler beendet.");
                    }

                    stopStream();
                    stopPolling();
                },
                onError: (e) => {
                    setJobState("error");
                    setErr(e?.message || String(e));
                    stopStream();
                    stopPolling();
                },
            });

            stopStreamRef.current = stopFn;
        } catch (e) {
            setJobState("error");
            setErr(e?.message || String(e));
        } finally {
            setStarting(false);
        }
    };

    // Cleanup beim Unmount
    useEffect(() => {
        return () => {
            stopStream();
            stopPolling();
        };
    }, []);

    const pill = useMemo(() => {
        if (jobState === "running") return <span className="badge badge--info">RUNNING</span>;
        if (jobState === "done") return <span className="badge badge--ok">DONE</span>;
        if (jobState === "error") return <span className="badge badge--err">ERROR</span>;
        return <span className="badge">IDLE</span>;
    }, [jobState]);

    return (
        <>
            <div className="card">
                <h1>Name Import</h1>
                <p className="muted">
                    Startet den Name-Import im Backend und zeigt Live-Logs über SSE. {pill}
                </p>

                <div className="form" style={{ maxWidth: 820 }}>
                    <label>
                        <div className="text-dim" style={{ fontSize: 13, marginBottom: 6 }}>
                            Google Sheet ID oder URL
                        </div>
                        <input
                            className="input"
                            value={sheetId}
                            onChange={(e) => setSheetId(e.target.value)}
                            placeholder="z.B. https://docs.google.com/spreadsheets/d/..."
                        />
                    </label>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <button className="btn btn--accent" onClick={startJob} disabled={starting}>
                            {starting ? "Starte…" : "Name-Import starten"}
                        </button>

                        <button className="btn" onClick={resetUI} disabled={starting && jobState === "running"}>
                            Reset
                        </button>

                        {jobId ? (
                            <span className="text-dim" style={{ fontSize: 13 }}>
                                Job: <strong>{jobId}</strong>
                            </span>
                        ) : null}
                    </div>

                    {err ? (
                        <div className="error-box" style={{ marginTop: 10 }}>
                            {err}
                        </div>
                    ) : null}

                    {/* Progress */}
                    {jobState !== "idle" ? (
                        <div style={{ marginTop: 10 }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    marginBottom: 6,
                                    alignItems: "center",
                                }}
                            >
                                <div className="text-dim" style={{ fontSize: 13 }}>
                                    {progress?.label ? progress.label : "Progress"}
                                </div>
                                <div className="text-dim" style={{ fontSize: 13 }}>
                                    {progress?.total ? `${progress.cur}/${progress.total}` : `${progressPct}%`}
                                </div>
                            </div>

                            <div
                                style={{
                                    height: 10,
                                    borderRadius: 999,
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    background: "rgba(255,255,255,0.06)",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        height: "100%",
                                        width: `${progressPct}%`,
                                        background: "var(--accent)",
                                        opacity: 0.9,
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}

                    {/* Logbox */}
                    <div style={{ marginTop: 12 }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "center",
                                marginBottom: 8,
                            }}
                        >
                            <div className="text-dim" style={{ fontSize: 13 }}>
                                Live Logs
                            </div>

                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    className="btn"
                                    onClick={() => {
                                        atBottomRef.current = true;
                                        logBoxRef.current?.scrollTo?.({ top: logBoxRef.current.scrollHeight });
                                    }}
                                    disabled={!logs.length}
                                >
                                    Scroll to bottom
                                </button>
                            </div>
                        </div>

                        <div
                            ref={logBoxRef}
                            onScroll={onLogScroll}
                            className="logbox"
                            style={{
                                maxHeight: 360,
                                overflow: "auto",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {logs.length ? (
                                logs.map((l, i) => {
                                    const lvl = String(l.level || "INFO").toUpperCase();
                                    const prefix =
                                        lvl === "ERROR" ? "[ERR] " : lvl === "WARN" ? "[WARN] " : "[INFO] ";

                                    return (
                                        <div key={i}>
                                            {prefix}
                                            {l.msg}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-dim">Noch keine Logs…</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}